import { BadgeCheckIcon } from '@heroicons/react/solid';
import { FaTelegram } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * Creating the bot with @BotFather, then pasting its token into the agent's
 * Telegram window. The token is a made-up, shortened one.
 */
export default function TelegramSetup({ botName }: { botName: string }) {
  const { t } = useChannelsT();

  const lines: { mine: boolean; text: string; mono?: boolean }[] = [
    { mine: true, text: '/newbot', mono: true },
    { mine: false, text: t('Alright, a new bot. What should it be called?') },
    { mine: true, text: botName },
    {
      mine: false,
      text: t('Good. Now choose a username. It must end in "bot".'),
    },
    { mine: true, text: 'VoltlineHelpBot', mono: true },
    {
      mine: false,
      text: t('Done! Use this token to connect your bot:'),
    },
  ];

  return (
    <div className='relative mx-auto max-w-[32rem] pb-2 sm:pb-24'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] text-start shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-3.5 dark:border-white/[0.06]'>
          <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[linear-gradient(150deg,#5ac8fa,#2a8bd8)] text-[13px] font-bold text-white'>
            BF
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm(
                'flex items-center gap-1 text-[15px] font-semibold',
                tone.ink,
              )}
            >
              BotFather
              <BadgeCheckIcon className='h-4 w-4 flex-none text-[#3390ec]' />
            </span>
            <span className={clsxm('block text-xs', tone.quiet)}>
              {t('bot')}
            </span>
          </span>
          <FaTelegram className='h-5 w-5 flex-none text-[#229ED9]' />
        </div>
        <div className='space-y-2 bg-[#eef3f7] px-4 py-4 text-[13px] leading-snug dark:bg-[#0f1c26]'>
          {lines.map(({ mine, text, mono }, index) => (
            <div
              key={index}
              className={clsxm('flex', mine ? 'justify-end' : 'justify-start')}
            >
              <p
                className={clsxm(
                  'max-w-[80%] rounded-[14px] px-3 py-1.5 shadow-[0_1px_1px_rgba(0,0,0,0.08)]',
                  mine
                    ? 'rounded-ee-[4px] bg-[#e1ffc7] text-[#000] dark:bg-[#2b5278] dark:text-white'
                    : 'rounded-es-[4px] bg-white text-[#000] dark:bg-[#182533] dark:text-white',
                  mono && 'font-mono text-[12.5px]',
                )}
              >
                {mono ? <span dir='ltr'>{text}</span> : text}
              </p>
            </div>
          ))}
          <div className='flex justify-start'>
            <p className='max-w-[80%] rounded-[14px] rounded-es-[4px] bg-white px-3 py-1.5 font-mono text-[12.5px] text-[#3390ec] shadow-[0_1px_1px_rgba(0,0,0,0.08)] dark:bg-[#182533] dark:text-[#6ab3f3]'>
              <span dir='ltr'>7104829…:AAHf…x9Q</span>
            </p>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[19rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p
          className={clsxm(
            'flex items-center gap-2 text-[14px] font-semibold',
            tone.ink,
          )}
        >
          <FaTelegram className='h-4 w-4 text-[#229ED9]' />
          {t('Telegram integration')}
        </p>
        <p className={clsxm('mt-3 text-xs font-medium', tone.quiet)}>
          {t('Bot token')}
        </p>
        <p
          className={clsxm(
            'mt-1.5 rounded-lg px-3 py-2 font-mono text-[12px] tracking-[0.2em] ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10',
            tone.ink,
          )}
        >
          ••••••••••••••••••
        </p>
        <div className='mt-3 flex items-center justify-between gap-3'>
          <span className={clsxm('flex items-center gap-2 text-xs', tone.body)}>
            <span className='relative h-5 w-9 flex-none rounded-full bg-primary-600'>
              <span className='absolute end-0.5 top-0.5 h-4 w-4 rounded-full bg-white' />
            </span>
            {t('Enable integration')}
          </span>
          <span className='flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            <CheckGlyph className='h-3.5 w-3.5' />
            {t('Connected')}
          </span>
        </div>
      </div>
    </div>
  );
}
